// src/services/adminTeamService.ts

import apiClient from "./apiClient";
import { TeamModel } from "../models/TeamModel";
import { PageResponse } from "../models/PaginationModel";

// Admin-specific endpoint — server-side filtering, sorting, includes inactive teams
const BASE_URL = "/api/admin/teams";

export interface AdminTeamFilters {
  search: string;
  sportType: string;             // "" = all, "FOOTBALL" | "BASKETBALL" | etc.
  gender: string;                // "" = all, "MALE" | "FEMALE" | "MIXED"
  active: "" | "true" | "false";
  clubId: number | "";           // "" = all clubs
  country: string;               // "" = all; club country code, "TR" / "ES" / etc.
  locked?: "" | "true" | "false";
  sortBy: string;
  sortDir: "asc" | "desc";
}

export interface CreateAdminTeamRequest {
  clubId: number;
  sportType: string;
  gender: string;
  // Sub-team code: null/blank = main team, "a", "1b", "u19", "paf", etc.
  // Must be lowercase letters, digits and hyphens only.
  teamCode?: string | null;
  displayName?: string;
}

export interface UpdateAdminTeamRequest {
  displayName?: string | null;   // null clears the custom name
  active?: boolean;
}

/**
 * Get paginated, filtered list of teams from the admin endpoint.
 * Server-side filtering — backend handles search, sport, gender, active, sort.
 */
export const getAdminTeams = async (
  page: number,
  pageSize: number,
  filters: AdminTeamFilters
): Promise<{ content: TeamModel[]; totalPages: number; totalItems: number }> => {
  const params = new URLSearchParams({
    page:    String(page),
    size:    String(pageSize),
    sortBy:  filters.sortBy  || "clubShortName",
    sortDir: filters.sortDir || "asc",
  });

  if (filters.search.trim())     params.set("search",    filters.search.trim());
  if (filters.sportType)         params.set("sportType", filters.sportType);
  if (filters.gender)            params.set("gender",    filters.gender);
  if (filters.active !== "")     params.set("active",    filters.active);
  if (filters.clubId !== "")     params.set("clubId",    String(filters.clubId));
  if (filters.country)           params.set("country",   filters.country);
  if (filters.locked)             params.set("locked",    filters.locked);

  const response = await apiClient.get<PageResponse<TeamModel>>(`${BASE_URL}?${params.toString()}`);

  return {
    content:    response.content    ?? [],
    totalPages: response.totalPages ?? 1,
    totalItems: response.totalItems ?? response.content?.length ?? 0,
  };
};

/** Get single team by ID */
export const getAdminTeamById = (id: number): Promise<TeamModel> =>
  apiClient.get<TeamModel>(`${BASE_URL}/${id}`);

/** Create a new team branch (ADMIN or MODERATOR) */
export const createAdminTeam = (data: CreateAdminTeamRequest): Promise<TeamModel> =>
  apiClient.post<TeamModel>(BASE_URL, data);

/** Update displayName / active flag (ADMIN or MODERATOR) */
export const updateAdminTeam = (id: number, data: UpdateAdminTeamRequest): Promise<TeamModel> =>
  apiClient.put<TeamModel>(`${BASE_URL}/${id}`, data);

/** Soft-delete a team (ADMIN only) */
export const deleteAdminTeam = (id: number): Promise<void> =>
  apiClient.delete<void>(`${BASE_URL}/${id}`);

/** Quick-toggle active flag — convenience wrapper for the table row toggle button */
export const toggleTeamActive = (id: number, active: boolean): Promise<TeamModel> =>
  apiClient.put<TeamModel>(`${BASE_URL}/${id}`, { active });

/** Lock or unlock a team topic — blocks/allows new entry submissions (ADMIN or MODERATOR) */
export const setTeamLocked = (id: number, locked: boolean, lockReason?: string, lockReasonVisible?: boolean): Promise<TeamModel> =>
  apiClient.patch<TeamModel>(`/api/admin/teams/${id}/lock`, { locked, lockReason, lockReasonVisible });